<template>
  <section class="new-pipeline-module-component-three module-container">
    <div class="inner-container">
      <div class="title">
        <h2
          class="title-inner"
          v-html="data.title" />
        <BicolorLine class="pipeline-bicolor-line" />
      </div>
      <div class="labels">
        <div>Focus</div>
        <div>Study Details</div>
        <div>Phase 1/2 Trial</div>
        <div>Phase 3 Registrational Trial</div>
      </div>
      <ul class="pipelines">
        <li
          v-for="(pipeLine, pipeIdx) in data.items"
          ref="pipeline"
          :key="'pipeline' + pipeIdx"
          class="pipeline">
          <div class="pipeline-title">
            <h3
              class="pipeline-title-inner"
              v-html="pipeLine.title" />
            <button
              v-show="pipeLine?.Text.length > 0 || pipeLine.cta_text.length > 0"
              class="pipeline-title-toggle"
              :class="{ active: open[pipeIdx] }"
              @click="toggle(pipeIdx)">
              <img src="@/assets/img/caret-down-progressive.svg" />
            </button>
          </div>
          <Vue3SlideUpDown
            v-model="open[pipeIdx]"
            class="pipeline-inner">
            <div class="pipeline-content-container">
              <div class="pipeline-inner-container w-full lg:w-1/2">
                <div
                  class="pipeline-inner-text max-w-[520px]"
                  v-html="pipeLine.Text" />
              </div>
              <div
                v-if="pipeLine.clinTrial"
                class="pipeline-inner-trial">
                <div
                  class="pipeline-inner-trial-text"
                  v-html="pipeLine.cta_text" />
                <a
                  :href="pipeLine.clinTrial.url"
                  target="_blank"
                  class="small-btn"
                  >{{ pipeLine.clinTrial.title }}</a
                >
              </div>
            </div>
          </Vue3SlideUpDown>
          <div class="pipeline-mobile">
            <div
              v-for="(item, itemIdx) in pipeLine.info"
              :key="'approach' + itemIdx"
              class="pipeline-mobile-approach">
              <div class="pipeline-mobile-approach-top">
                <div class="kvp approach">
                  <div
                    v-if="item.approach_area.add_approach"
                    class="label approach">
                    <strong>{{ item.approach_area.approach }}</strong>
                  </div>
                  <div
                    v-for="(sub, subIdx) in item.sub_category"
                    :key="'study-' + subIdx"
                    class="study-details">
                    <div
                      v-for="(study, studyIdx) in sub.focus"
                      :key="`study-${studyIdx}`"
                      class="study-container"
                      :class="`${
                        subIdx === item.sub_category.length - 1
                          ? 'last-child'
                          : ''
                      }`">
                      <div class="inner-container">
                        <div class="focus-and-details">
                          <div class="focus-area">
                            <strong>Focus: </strong>
                            <strong>Study Details: </strong>
                          </div>
                          <div class="study">
                            <span>{{ sub.focus_area }}</span>
                            <span>{{ study.type }}</span>
                          </div>
                        </div>
                        <div class="image">
                          <img
                            v-if="study.image"
                            :src="study.image.url"
                            :alt="study.image.alt" />
                        </div>
                      </div>
                      <div class="progress-bar-area">
                        <div
                          class="progress-bar-container"
                          :style="getBarStyle(study)">
                          <div
                            class="progress-bar-area relative"
                            :style="{
                              maxWidth: `${study.prog_mob}%`,
                            }">
                            <div class="progress-bar z-1" />
                            <p
                              class="text-[12px] absolute -top-0.5 right-2 z-2"
                              :class="study.font_color">
                              {{ study.bar_content }}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div class="bottom-stage text-[10px]">
                        {{ study.stage }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="pipeline-desktop">
            <div
              v-for="(item, itemIdx) in pipeLine.info"
              :key="'dt-approach' + itemIdx"
              class="pipeline-desktop-item">
              <div
                v-if="item.approach_area.add_approach"
                class="parent">
                <strong>{{ item.approach_area.approach }}</strong>
              </div>
              <div
                v-for="(sub, subIdx) in item.sub_category"
                :key="`sub-${subIdx}`"
                class="children">
                <div class="focus-area">
                  {{ sub.focus_area }}
                </div>
                <div class="stats-container">
                  <div
                    v-for="(child, childIdx) in sub.focus"
                    :key="'dt-child' + childIdx"
                    class="stats-inner-container">
                    <div class="left-part">
                      <div
                        v-if="!child.image_and_text"
                        class="child">
                        <div class="empty"></div>
                        <span v-html="child.type" />
                      </div>
                      <div
                        v-else
                        class="img-container">
                        <img
                          class="child-img"
                          :src="child.image.url"
                          :alt="child.image.alt" />
                        <span class="child">
                          {{ child.type }}
                        </span>
                      </div>
                    </div>
                    <div class="progress">
                      <div
                        class="progress-bar-container"
                        :style="getBarStyle(child)">
                        <div
                          class="progress-bar-outer relative"
                          :style="{
                            width: `${child.prog}%`,
                          }">
                          <div class="progress-bar z-1" />
                          <p
                            class="text-[12px] absolute -top-0.5 right-2 z-2"
                            :class="child.font_color">
                            {{ child.bar_content }}
                          </p>
                        </div>
                      </div>
                      <div class="divider" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>
      <div class="disclaimer pt-1 mb-0 pb-0">
        <p
          class="footnotes"
          v-html="data.footnotes" />
      </div>
      <div
        v-if="data.bottom_area"
        class="other mt-4 bg-white p-6 rounded-xl">
        <div class="other-left">
          <div
            class="other-left-text font-extrabold"
            v-html="data.other.bottom_title" />
          <BicolorLine class="pipeline-bicolor-line my-5" />
        </div>
        <div class="other-right">
          <div
            class="other-right-content"
            v-html="data.other.bottom_text" />
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import BicolorLine from '@/components/utility/BicolorLine.vue'
import Vue3SlideUpDown from 'vue3-slide-up-down'
import { onMounted, onBeforeUnmount, ref } from 'vue'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const pipeline = ref()

const open = ref([])

defineProps(['data'])

const toggle = (idx) => {
  open.value[idx] = !open.value[idx]
}

const animateDesktop = () => {
  gsap.fromTo(
    '.new-pipeline-module-component-three .pipeline-desktop .progress-bar',
    {
      width: '0%',
    },
    {
      scrollTrigger: {
        trigger: '.new-pipeline-module-component-three',
        element: '.progress-bar',
        start: 'top 80%',
        end: 'bottom 80%',
        markers: false,
        id: 'new-values',
      },
      width: '100%',
      duration: 1.5,
      ease: 'power2.out',
      stagger: 0.2,
    }
  )
}

const animateMobile = () => {
  gsap.fromTo(
    '.new-pipeline-module-component-three .pipeline-mobile .progress-bar',
    {
      width: '0%',
    },
    {
      scrollTrigger: {
        trigger: '.new-pipeline-module-component-three',
        element: '.progress-bar',
        start: 'top 80%',
        end: 'bottom 80%',
        markers: false,
        id: 'new-values-mob',
      },
      width: '100%',
      duration: 1.5,
      ease: 'power2.out',
      stagger: 0.2,
    }
  )
}

const animateOnResize = () => {
  if (window.innerWidth > 1024) {
    animateDesktop()
  } else {
    animateMobile()
  }
  window.removeEventListener('resize', animateOnResize)
}

onMounted(() => {
  animateOnResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', animateOnResize)
  const anim = ScrollTrigger.getById('new-values')
  if (anim) anim.kill()
  const animMob = ScrollTrigger.getById('new-values-mob')
  if (animMob) animMob.kill()
})

const getBarStyle = (item) => {
  return {
    '--barto': `${item.color_from}`,
    '--barfrom': `${item.color_to}`,
  }
}

onBeforeUnmount(() => {
  const anim = ScrollTrigger.getById('new-values')
  if (anim) anim.kill()
})

onMounted(() => {
  if (pipeline.value) {
    pipeline.value.forEach(() => {
      open.value.push(false)
    })
  }
})
</script>
<style lang="scss">
.new-pipeline-module-component-three {
  @apply py-[30px];

  .small-btn {
    @apply flex items-center justify-center bg-progressive px-4 rounded-full h-[35px] text-[12px] xl:text-[14px] scale-100 transition-transform hover:scale-95;
    @apply text-center text-base not-italic font-normal leading-[22px] tracking-[-0.16px];
    @apply lg:w-[180px];
  }

  @apply bg-functional;

  .progress-bar-container {
    @apply w-full h-full bg-[#d3d8d8] lg:bg-transparent rounded-[500px] relative z-2;
    .progress-bar {
      @apply h-5 rounded-[10px];
      background: linear-gradient(
        270deg,
        var(--barfrom) -100%,
        var(--barto) 100%
      );
    }
  }

  .pipeline-bicolor-line {
    @apply w-[66px] h-[3px];
  }

  .inner-container {
    @apply px-[10px];

    .title {
      @apply flex flex-col gap-1 bg-white rounded-[10px] py-[15px] px-[10px] my-[5px] lg:px-[30px] lg:py-6;

      &-inner {
        @apply text-balanced text-base font-bold leading-5;
        @apply lg:text-[26px] lg:not-italic lg:font-medium lg:leading-[30px];
      }
    }

    .labels {
      @apply hidden -mt-[5px] mb-[5px] py-[5px] lg:flex rounded-[10px] bg-progressive;

      div {
        @apply flex items-center justify-center h-11 text-center border-black border-r-[1px] text-humble;

        &:first-child {
          @apply w-[15%];
        }

        &:nth-child(2) {
          @apply w-[30%];
        }

        &:nth-child(3) {
          @apply w-[27.5%];
        }

        &:nth-child(4) {
          @apply w-[27.5%] border-none;
        }
      }
    }

    .pipelines {
      @apply flex flex-col gap-4 lg:gap-[5px];

      .pipeline {
        @apply bg-white rounded-[10px] overflow-hidden;

        &-title {
          @apply min-h-[53px] flex items-center relative bg-approachable py-[14px] pl-3 pr-2 lg:pt-[10px] lg:pb-2 lg:px-[30px] lg:min-h-[38px];

          &-inner {
            @apply w-full max-w-[92%];

            p {
              @apply lg:text-base lg:not-italic lg:font-normal lg:leading-5;
            }
          }

          &-toggle {
            @apply absolute top-[14px] right-[8px] w-[25px] h-[25px] bg-white rounded-full shadow-md flex items-center justify-center lg:top-[6px] lg:right-[7px];

            img {
              @apply w-[10px] transition-all;
            }

            &.active {
              img {
                @apply rotate-180;
              }
            }
          }
        }

        &-inner {
          @apply border-b border-solid border-functional;
          background: linear-gradient(180deg, #70ffe5 11.89%, #fff 56.45%);
          .pipeline-content-container {
            @apply flex flex-col gap-8 pl-3 pr-[15px] lg:flex-row lg:px-[30px] lg:items-center pb-4 lg:pb-0;
          }
          &-text {
            @apply mt-10 text-base not-italic font-normal leading-[22px] tracking-[-0.16px] lg:mb-[60px] flex flex-col gap-2;
            p {
              @apply text-[16px] lg:text-[18px];
            }
          }

          &-trial {
            @apply flex flex-col gap-3 items-start border border-solid bg-white border-functional rounded-[10px] lg:mt-10 lg:mb-[50px] lg:min-w-[434px] lg:max-w-[434px] px-[30px] lg:px-[25px] py-[25px] mx-2 lg:mx-0;

            &-text {
              @apply text-base not-italic font-normal leading-[22px] tracking-[-0.16px];
            }
          }
        }

        &-mobile {
          @apply my-0.5 flex flex-col lg:hidden;

          &-approach {
            @apply flex flex-col gap-2;

            &:last-child {
              @apply pb-0 mb-0 border-b-0;
            }

            &-top {
              @apply flex flex-col;

              .kvp {
                @apply flex flex-col;

                .label {
                  @apply p-3 rounded-lg bg-functional text-humble header-6 w-full;
                }
                .study-details {
                  .study-container {
                    &:not(.last-child) {
                      @apply border-b border-solid border-gray_green_trans;
                    }
                    @apply flex flex-col py-2 px-3;
                    .inner-container {
                      @apply flex py-2 flex-row items-center justify-between gap-2;
                      .focus-and-details {
                        @apply flex flex-row gap-4;
                        .focus-area {
                          @apply flex min-w-[90px] flex-col text-[13px];
                        }
                        .study {
                          @apply flex min-w-[140px] flex-col text-[13px];
                        }
                      }
                      .image {
                        @apply w-24 h-auto relative;
                        img {
                          @apply absolute -left-5 -top-5 w-24 max-w-none h-auto object-contain;
                        }
                      }
                    }
                    .progress-bar {
                    }
                    .bottom-stage {
                      @apply text-[10px] text-humble font-normal py-1 pl-3;
                    }
                  }
                }
              }
            }

            &-bottom {
              @apply flex flex-col gap-1 mx-[10px];

              .stage {
                @apply px-[11px] text-xs not-italic font-normal leading-[120%] tracking-[-0.24px];
              }
            }
          }
        }

        &-desktop {
          @apply hidden lg:flex flex-col;

          &-item {
            @apply flex flex-col;

            @mixin spreadsheet {
              border-bottom: 1px solid #d3d8d8;
              border-left: 1px solid #d3d8d8;
              margin-top: -1px;
              margin-left: -1px;
            }

            .parent {
              @apply w-full flex px-8 py-[3px] bg-functional rounded-[9px] justify-start items-start text-left text-humble;

              &:first-child {
                @apply border-l-0 border-b-0;
              }
            }

            .children {
              @apply w-full;
              &:last-child {
                .focus-area {
                  border-bottom: 0 !important;
                }
                .stats-container {
                  .stats-inner-container {
                    &:last-child {
                      .left-part {
                        border-bottom: 0 !important;
                      }
                      .progress {
                        border-bottom: 0 !important;
                      }
                    }
                  }
                }
              }
              .focus-area {
                @include spreadsheet;
                @apply font-medium flex items-center justify-center w-[15%];
              }
              @apply flex flex-row;
              .stats-container {
                @apply flex flex-col w-[85%];
                .stats-inner-container {
                  @apply flex w-full py-0 min-h-[30px];

                  .left-part {
                    @apply w-[36%] px-1 flex items-center min-h-[30px];
                    @include spreadsheet;
                    .child {
                      @apply border-none flex justify-between w-full;
                      @apply px-[6.5px] py-[1px];

                      span {
                        @apply text-right;
                      }
                    }
                    .img-container {
                      @apply flex justify-between w-full;
                      @apply flex items-center;
                      .child-img {
                        @apply w-fit object-contain object-left h-[100%] max-h-[20px] mr-2;
                      }
                      .child {
                        @apply w-fit;
                      }
                      .superscript {
                        @apply relative text-[12px] leading-tight px-0 -left-2.5 -top-1;
                      }
                    }
                  }
                  .progress {
                    @apply relative flex flex-col items-center w-[66%] h-full;
                    @include spreadsheet;

                    .progress-bar-container {
                      @apply flex items-center;
                      .progress-bar-outer {
                        @apply h-5;
                        .progress-bar {
                          @apply relative z-2 h-full rounded-[500px];
                        }
                      }
                    }
                    .divider {
                      @apply absolute w-px h-full bg-[#d3d8d8] left-1/2 translate-x-[-50%] z-1;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    .disclaimer {
      @apply pt-4 pl-0 xl:pl-3 max-w-[860px];
      * {
        @apply leading-tight italic text-[16px] font-medium text-left pb-2;
      }
    }
  }

  .other {
    .other-right {
      .other-right-content {
        ul {
          @apply list-disc;
          li::marker {
            @apply text-progressive;
          }
        }
      }
    }
  }
}
</style>
